import { FormikProps } from 'formik';
import { CreateActivityFormData } from '../../../../../../types/activity.types';
import { getFieldInputType, getFieldsForActivity, shouldShowField } from '../../../../../../types/activity.config';
import CountrySelect from '../../../../../../common/CountrySelect';
import AirportSelect from '../../../../../../common/AirportSelect';
import AirlineSelect from '../../../../../../common/AirlineSelect';
import GooglePlacesInput from '../../../../../../common/GooglePlacesInput';
import CustomInput from '../../../../../../common/CustomInput';
import { isPartialAmountSpentInput } from '../../../../../../utils/amountUtils';

/**
 * Hook for rendering form fields with consistent logic
 * @param formik - Formik props object
 * @param activityType - Current activity type
 * @returns Object with field rendering utilities
 */
export const useFormFieldRenderer = (formik: FormikProps<CreateActivityFormData>, activityType: string) => {
  const { values, handleChange, errors, touched, setFieldValue, setFieldTouched } = formik;

  const isFieldVisible = (fieldName: string) => {
    return shouldShowField(fieldName, activityType);
  };

  const isFieldMandatory = (fieldName: string) => {
    return getFieldsForActivity(activityType).mandatory.includes(fieldName);
  };

  const getError = (fieldName: keyof CreateActivityFormData) => {
    return touched[fieldName] && errors[fieldName] ? String(errors[fieldName]) : '';
  };

  const renderFieldInput = (fieldName: keyof CreateActivityFormData, label: string, placeholder?: string) => {
    const inputType = getFieldInputType(fieldName as string);
    const fieldValue = values[fieldName] !== undefined && values[fieldName] !== null ? String(values[fieldName]) : '';
    const error = getError(fieldName);

    switch (inputType) {
      case 'country':
        return (
          <CountrySelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            error={error}
          />
        );

      case 'airport':
        return (
          <AirportSelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            onDetailsChange={details => {
              setFieldValue(`${fieldName}_details`, details);
            }}
            details={values[`${fieldName}_details` as keyof CreateActivityFormData] as any}
            error={error}
          />
        );

      case 'airline':
        return (
          <AirlineSelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            onDetailsChange={details => {
              setFieldValue(`${fieldName}_details`, details);
            }}
            details={values[`${fieldName}_details` as keyof CreateActivityFormData] as any}
            error={error}
          />
        );

      case 'address':
        return (
          <GooglePlacesInput
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={(address: string, placeDetails?: google.maps.places.PlaceResult) => {
              setFieldValue(fieldName, address);
              if (placeDetails) {
                setFieldValue(`${fieldName}_details`, placeDetails);
              } else {
                setFieldValue(`${fieldName}_details`, null);
              }
            }}
            onBlur={() => setFieldTouched(fieldName, true)}
            placeholder={placeholder || 'Enter address...'}
            error={!!error}
            helperText={error}
          />
        );

      default:
        return (
          <CustomInput
            inputName={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            error={error}
            placeholder={placeholder}
          />
        );
    }
  };

  const renderAmountSpentInput = () => {
    const error = getError('amountSpent');
    const rawValue =
      values.amountSpent !== undefined && values.amountSpent !== null ? String(values.amountSpent) : '';

    return (
      <CustomInput
        inputName="amountSpent"
        inputLabel="Amount Spent"
        value={rawValue}
        onChange={e => {
          const next = e.target.value;
          if (isPartialAmountSpentInput(next)) {
            setFieldValue('amountSpent', next);
          }
        }}
        onBlur={() => setFieldTouched('amountSpent', true, true)}
        error={error}
        placeholder="0.00"
      />
    );
  };

  return {
    isFieldVisible,
    isFieldMandatory,
    getError,
    renderFieldInput,
    renderAmountSpentInput,
  };
};
